import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Wallet, FileCheck2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MintModal({ open, onOpenChange, onMinted }) {
  const { user, walletAddress, connectWallet } = useAuth();
  const [step, setStep] = useState(1);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classId, setClassId] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [uploadedType, setUploadedType] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contractType, setContractType] = useState("ERC-721");
  const [editionCount, setEditionCount] = useState(1);
  const [listType, setListType] = useState("fixed");
  const [priceEth, setPriceEth] = useState("");
  const [auctionDays, setAuctionDays] = useState(7);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setUploadedPath(null);
    setFile(null);
    setTitle("");
    setDescription("");
    api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!classId) {
      setAssignments([]);
      return;
    }
    api
      .get("/assignments", { params: { class_id: classId } })
      .then(({ data }) => setAssignments(data))
      .catch(() => {});
  }, [classId]);

  const handleUpload = async () => {
    if (!file) return toast.error("Select a file first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedPath(data.path);
      setUploadedType(data.content_type);
      toast.success("File uploaded to storage");
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleMint = async () => {
    if (!walletAddress) {
      try {
        await connectWallet();
      } catch {
        return toast.error("Connect wallet first");
      }
    }
    setBusy(true);
    try {
      const auctionEnd =
        listType === "auction"
          ? new Date(Date.now() + Number(auctionDays) * 86400000).toISOString()
          : null;
      const payload = {
        title,
        description,
        file_path: uploadedPath,
        file_type: uploadedType || "application/octet-stream",
        original_filename: file?.name || "asset",
        class_id: classId || null,
        assignment_id: assignmentId || null,
        contract_type: contractType,
        edition_count: contractType === "ERC-1155" ? Number(editionCount) : 1,
        list_type: listType,
        price_eth: listType === "fixed" ? Number(priceEth) : null,
        auction_end: auctionEnd,
        wallet_address: walletAddress || "0x0",
        wallet_signature: `sig_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      };
      const { data } = await api.post("/assets/mint", payload);
      toast.success("Minted! " + (data.approval_status === "pending" ? "Awaiting teacher approval." : "Listed on marketplace."));
      onOpenChange(false);
      onMinted && onMinted(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isTeacher = user?.role === "teacher";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Mint New Asset</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {isTeacher ? "Mint a lesson plan or asset." : "Attach your work to a class & assignment, then mint to blockchain."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 my-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded ${step >= s ? "bg-cyan8r" : "bg-white/10"}`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4 anim-fade-up">
            {!isTeacher && (
              <>
                <div>
                  <Label className="text-zinc-300">Class</Label>
                  <Select value={classId} onValueChange={setClassId}>
                    <SelectTrigger data-testid="mint-class-select" className="bg-[#0A0A0A] border-white/10 mt-1">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                      {classes.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No classes — join one first</div>}
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Assignment</Label>
                  <Select value={assignmentId} onValueChange={setAssignmentId} disabled={!classId}>
                    <SelectTrigger data-testid="mint-assignment-select" className="bg-[#0A0A0A] border-white/10 mt-1">
                      <SelectValue placeholder="Select assignment" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                      {assignments.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No assignments yet</div>}
                      {assignments.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button
              data-testid="mint-step1-next"
              onClick={() => setStep(2)}
              className="bg-white text-black hover:bg-zinc-200 w-full"
            >
              Next: Upload file
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 anim-fade-up">
            <label className="block">
              <div className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/10 hover:border-cyan8r/40 transition rounded-lg py-12 cursor-pointer bg-[#0A0A0A]">
                <Upload size={28} className="text-zinc-400" />
                <div className="text-sm text-zinc-300">
                  {file ? file.name : "Click to upload any file"}
                </div>
                <div className="text-xs text-zinc-500">Image · Video · Audio · PDF · 3D · Code · anything</div>
                <input
                  data-testid="mint-file-input"
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </label>
            {file && (
              <div className="text-xs text-zinc-500 flex items-center gap-2">
                <FileCheck2 size={14} className="text-cyan8r" />
                {file.name} · {(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}
              </div>
            )}
            <Button
              data-testid="mint-upload-btn"
              onClick={handleUpload}
              disabled={!file || busy}
              className="bg-white text-black hover:bg-zinc-200 w-full"
            >
              {busy ? <><Loader2 className="animate-spin mr-2" size={16} /> Uploading…</> : "Upload to storage"}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 anim-fade-up max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-zinc-300">Title</Label>
              <Input
                data-testid="mint-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 mt-1"
                placeholder="My masterpiece"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                data-testid="mint-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 mt-1"
                rows={3}
                placeholder="Tell collectors about it..."
              />
            </div>
            <div>
              <Label className="text-zinc-300">Contract type</Label>
              <RadioGroup value={contractType} onValueChange={setContractType} className="grid grid-cols-2 gap-3 mt-2">
                <label className={`p-3 rounded-md border cursor-pointer ${contractType === "ERC-721" ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
                  <RadioGroupItem value="ERC-721" className="sr-only" />
                  <div className="font-mono text-sm">ERC-721</div>
                  <div className="text-xs text-zinc-500">1 of 1</div>
                </label>
                <label className={`p-3 rounded-md border cursor-pointer ${contractType === "ERC-1155" ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
                  <RadioGroupItem value="ERC-1155" className="sr-only" />
                  <div className="font-mono text-sm">ERC-1155</div>
                  <div className="text-xs text-zinc-500">Batch / editions</div>
                </label>
              </RadioGroup>
            </div>
            {contractType === "ERC-1155" && (
              <div>
                <Label className="text-zinc-300">Edition count</Label>
                <Input
                  type="number"
                  min={1}
                  value={editionCount}
                  onChange={(e) => setEditionCount(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 mt-1"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-300">Listing</Label>
              <RadioGroup value={listType} onValueChange={setListType} className="grid grid-cols-3 gap-3 mt-2">
                {["fixed", "auction", "none"].map((opt) => (
                  <label key={opt} className={`p-2 rounded-md border text-center cursor-pointer text-sm capitalize ${listType === opt ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
                    <RadioGroupItem value={opt} className="sr-only" />
                    {opt === "none" ? "No listing" : opt}
                  </label>
                ))}
              </RadioGroup>
            </div>
            {listType === "fixed" && (
              <div>
                <Label className="text-zinc-300">Price (ETH)</Label>
                <Input
                  data-testid="mint-price-input"
                  type="number"
                  step="0.001"
                  value={priceEth}
                  onChange={(e) => setPriceEth(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 mt-1"
                  placeholder="0.05"
                />
              </div>
            )}
            {listType === "auction" && (
              <div>
                <Label className="text-zinc-300">Auction duration (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={auctionDays}
                  onChange={(e) => setAuctionDays(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 mt-1"
                />
              </div>
            )}

            <div className="border border-white/10 rounded-md p-3 bg-[#0A0A0A]">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Profit split</div>
              <div className="flex h-2 rounded overflow-hidden">
                <div className="bg-cyan8r" style={{ width: isTeacher ? "98%" : "97%" }} />
                {!isTeacher && <div className="bg-emerald-400" style={{ width: "1%" }} title="Teacher" />}
                <div className="bg-amber-400" style={{ width: "1%" }} title="School" />
                <div className="bg-violet-400" style={{ width: "1%" }} title="Cure8r" />
              </div>
              <div className="flex justify-between text-[10px] mt-2 text-zinc-500 font-mono">
                <span>You {isTeacher ? "98%" : "97%"}</span>
                {!isTeacher && <span>Teacher 1%</span>}
                <span>School 1%</span>
                <span>Cure8r 1%</span>
              </div>
            </div>

            <button
              data-testid="mint-finalize-btn"
              onClick={handleMint}
              disabled={busy || !title}
              className="btn-web3 w-full py-3 rounded-md text-sm font-medium flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Wallet size={16} />}
              {walletAddress ? "Sign & Mint" : "Connect Wallet & Mint"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
